// Shared theme configuration & helpers for the Appearance customizer.

export const DEFAULT_THEME = {
  primary: "#0e9083",
  accent: "#fa7b19",
  secondary: "#3cd7b0",
  background: "#e3f2ef",
  text_color: "#0f2422",
  heading_font: "Clash Display",
  body_font: "General Sans",
  display_font: "Clash Display",
  base_font_size: 16,
  line_height: 1.7,
};

// Fonts that are already bundled via Fontshare (index.css) — no dynamic load needed.
export const NON_GOOGLE_FONTS = ["Clash Display", "General Sans"];

// Curated modern font library (Google Fonts, grouped by category).
export const FONT_LIBRARY = [
  { category: "Signature", fonts: ["Clash Display", "General Sans"] },
  {
    category: "Sans-serif",
    fonts: [
      "Inter", "Poppins", "Montserrat", "Work Sans", "DM Sans", "Manrope",
      "Space Grotesk", "Sora", "Outfit", "Plus Jakarta Sans", "Figtree",
      "Lexend", "Rubik", "Nunito", "Raleway", "Urbanist", "Onest", "Epilogue",
      "Red Hat Display", "Archivo", "Mulish", "Karla", "Quicksand", "Josefin Sans",
    ],
  },
  {
    category: "Serif",
    fonts: [
      "Playfair Display", "Merriweather", "Lora", "Cormorant Garamond",
      "EB Garamond", "Libre Baskerville", "Fraunces", "Spectral", "Bitter",
      "PT Serif", "Source Serif 4", "Crimson Pro",
    ],
  },
  {
    category: "Display",
    fonts: ["Bricolage Grotesque", "Syne", "Unbounded", "Space Mono"],
  },
];

// Ready-made colour palettes.
export const COLOR_PRESETS = [
  { name: "Peoplelab", primary: "#0e9083", accent: "#fa7b19", secondary: "#3cd7b0", background: "#e3f2ef" },
  { name: "Midnight Teal", primary: "#0f766e", accent: "#f59e0b", secondary: "#5eead4", background: "#ecfdf5" },
  { name: "Royal Indigo", primary: "#4f46e5", accent: "#f59e0b", secondary: "#a5b4fc", background: "#eef2ff" },
  { name: "Sunset Coral", primary: "#e11d48", accent: "#f59e0b", secondary: "#fda4af", background: "#fff1f2" },
  { name: "Forest", primary: "#15803d", accent: "#ca8a04", secondary: "#86efac", background: "#f0fdf4" },
  { name: "Ocean", primary: "#0369a1", accent: "#f97316", secondary: "#7dd3fc", background: "#eff6ff" },
  { name: "Plum", primary: "#7e22ce", accent: "#ec4899", secondary: "#d8b4fe", background: "#faf5ff" },
  { name: "Charcoal Amber", primary: "#1f2937", accent: "#f59e0b", secondary: "#9ca3af", background: "#f9fafb" },
  { name: "Rose Gold", primary: "#be123c", accent: "#d97706", secondary: "#fda4af", background: "#fff7ed" },
];

// Convert "#rrggbb" to a Tailwind CSS-var compatible "H S% L%" string.
export function hexToHslString(hex) {
  let h = (hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return "0 0% 0%";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const lig = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    sat = lig > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4; break;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(lig * 100)}%`;
}

const _loaded = new Set();

// Dynamically load a Google font (skips bundled Fontshare fonts).
export function loadGoogleFont(name) {
  if (!name || NON_GOOGLE_FONTS.includes(name) || _loaded.has(name)) return;
  _loaded.add(name);
  const id = "gf-" + name.toLowerCase().replace(/\s+/g, "-");
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    name
  ).replace(/%20/g, "+")}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

// Apply a full theme object to the document root (used on the public site).
export function applyTheme(t) {
  if (!t) return;
  const root = document.documentElement;
  root.style.setProperty("--primary", hexToHslString(t.primary));
  root.style.setProperty("--ring", hexToHslString(t.primary));
  root.style.setProperty("--accent", hexToHslString(t.accent));
  root.style.setProperty("--secondary", hexToHslString(t.secondary));
  root.style.setProperty("--background", hexToHslString(t.background));
  if (t.text_color) {
    const fg = hexToHslString(t.text_color);
    root.style.setProperty("--foreground", fg);
    root.style.setProperty("--card-foreground", fg);
    root.style.setProperty("--popover-foreground", fg);
  }
  if (t.heading_font) root.style.setProperty("--font-heading", `"${t.heading_font}"`);
  if (t.body_font) root.style.setProperty("--font-body", `"${t.body_font}"`);
  if (t.display_font) root.style.setProperty("--font-display", `"${t.display_font}"`);
  if (t.base_font_size) document.body.style.fontSize = `${t.base_font_size}px`;
  if (t.line_height) document.body.style.lineHeight = `${t.line_height}`;
  loadGoogleFont(t.heading_font);
  loadGoogleFont(t.body_font);
  loadGoogleFont(t.display_font);
}
